import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Paths where the public newsletter popup must NOT appear
const HIDE_ON_PREFIXES = ['/admin', '/master', '/chatbot', '/marketing', '/humanresources', '/hr'];

const NewsletterPopup = () => {
  const location = useLocation();
  const isInternalRoute = HIDE_ON_PREFIXES.some(p => location.pathname.startsWith(p));
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    whatsapp_number: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isInternalRoute) {
      // Make sure popup is hidden on internal/admin pages
      setIsVisible(false);
      return;
    }
    // Check if user has already seen the popup in this session
    const hasSeenPopup = sessionStorage.getItem('newsletter_popup_seen');

    if (!hasSeenPopup) {
      // Show popup after 10 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [isInternalRoute]);

  if (isInternalRoute) return null;

  const handleClose = () => {
    setIsVisible(false);
    // Mark as seen for this session only
    sessionStorage.setItem('newsletter_popup_seen', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/newsletter/subscribe`, formData);
      toast.success('Successfully subscribed! We will keep you updated.');
      handleClose();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('You are already subscribed with this WhatsApp number!');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-brand-navy/60 z-50 transition-opacity duration-300"
        onClick={handleClose}
      ></div>

      {/* Popup */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md px-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 relative animate-fade-in">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
            data-testid="newsletter-close-btn"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>

          {/* Content */}
          <div className="mb-6">
            <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
              Stay Updated! 📢
            </h3>
            <p className="text-muted-foreground">
              Sign up for regular health tips and hospital updates
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Full Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-border bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter your full name"
                data-testid="newsletter-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                WhatsApp Number <span className="text-destructive">*</span>
              </label>
              <input
                type="tel"
                value={formData.whatsapp_number}
                onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-border bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="10-digit WhatsApp number"
                pattern="[0-9]{10}"
                data-testid="newsletter-whatsapp-input"
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary/90 active:scale-95 transition-all duration-200 disabled:opacity-50"
                data-testid="newsletter-submit-btn"
              >
                {submitting ? 'Subscribing...' : 'Sign Up'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-3 rounded-lg border border-border hover:bg-slate-50 transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </form>

          <p className="text-xs text-muted-foreground mt-4 text-center">
            We respect your privacy. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </>
  );
};

export default NewsletterPopup;
